// src/pages/financial/utils/financialApi.ts

const API = process.env.REACT_APP_API_BASE_URL;

export type CourseRevenue = {
  courseName: string;
  revenue: number;
  students: number;
  collectionRate: number;
  revenuePerStudent?: number;
};

export type ExpenseCategory = {
  category: string;
  percentage: number;
  amount: number;
};

export type MonthlyPoint = { month: string; revenue: number; costs: number };

export type ExpenseCategoryValue =
  | "SALARY"
  | "SUBSCRIPTION"
  | "REFUND"
  | "OTHER";

export type Partner = "AKASH" | "JEWEL" | "ZAFOR";

export type WithdrawalRow = {
  id: number;
  partner: Partner;
  amount: number;
  date?: string;
  withdrawalDate?: string;
  createdByName?: string;
  createdById?: number;
  createdAt?: string;
};

export type ExpenseRow = {
  id: number;
  category: ExpenseCategoryValue;
  amount: number;
  description?: string;
  expenseDate?: string;
  date?: string;
  createdByName?: string;
  createdById?: number;
  createdAt?: string;
};

export type FinancialActivityType =
  | "PAYMENT_RECEIVED"
  | "EXPENSE"
  | "WITHDRAWAL"
  | "REFUND";

export type FinancialActivity = {
  id: string;
  date: string;
  type: FinancialActivityType;
  amount: number;
  category?: string;
  description?: string;
  relatedTo?: string;
  relatedToFull?: string;
  paymentMethod?: string;
  createdBy?: string;
  status?: string;
};

// ===== API Functions =====

export const fetchFinancialOverview = async () => {
  const response = await fetch(`${API}/api/financial/overview`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch financial overview");
  return response.json();
};

export const fetchRevenueDistribution = async () => {
  const response = await fetch(`${API}/api/financial/distribution`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch revenue distribution");
  return response.json();
};

export const fetchExpenseBreakdown = async () => {
  const response = await fetch(`${API}/api/financial/expenses`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch expense breakdown");
  return response.json();
};

export const fetchWithdrawalHistory = async () => {
  const response = await fetch(`${API}/api/financial/withdrawals`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch withdrawal history");
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const fetchExpenseHistory = async () => {
  const response = await fetch(`${API}/api/expenses`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error("Failed to fetch expense history");
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const fetchFinancialActivities = async () => {
  const response = await fetch(`${API}/api/financial/activities`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch financial activities");
  return response.json();
};

export const createExpense = async (payload: {
  category: ExpenseCategoryValue;
  amount: number;
  description?: string;
  expenseDate: string;
}) => {
  const res = await fetch(`${API}/api/expenses`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(
      `Failed to create expense (${res.status}): ${msg || "Unknown error"}`
    );
  }
  return res.json();
};

export const createWithdrawal = async (payload: {
  partner: string;
  amount: number;
  date: string;
}) => {
  const res = await fetch(`${API}/api/financial/withdrawals`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(t || "Failed to record withdrawal.");
  }
  return res.json();
};

export const deleteWithdrawal = async (id: number) => {
  const res = await fetch(`${API}/api/financial/withdrawals/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(t || "Failed to delete withdrawal");
  }
};

export const deleteExpense = async (id: number) => {
  const res = await fetch(`${API}/api/expenses/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(t || "Failed to delete expense");
  }
};

// ===== Utility Functions =====

export const formatCurrency = (amount: number) =>
  `৳${Math.round(amount).toLocaleString()}`;

export const fmtDate = (s?: string) => {
  if (!s) return "-";
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toISOString().slice(0, 10);
};
